import React from 'react';
import { FormControl, Grid2, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomTextField from '../forms/theme-elements/CustomTextField';
import { useAppSelector } from '../../store';
import ShowContainer from './ShowContainer';
import CusTooltip from './CusTooltip';
import { dispatchCusEvent } from '../../utils';
import { ENUM_CUS_EVENT, ENUM_TOAST_TYPE } from '../../enums/enums';
import { IconCopy } from '@tabler/icons-react';
import useToast from '../../hooks/useToast';

export default function CusInput({
  id,
  label,
  value = undefined,
  onChange,
  type = 'text',
  rows,
  isRow,
  isRowReverse,
  subLabel,
  inputProps = {},
  slotProps = {},
  disabled,
  tooltipText,
  ref,
  width,
  readOnly,
  placeholder,
  inputSx,
  onClick,
  maxLength,
  name,
  textValuesName,
  isCopyClick,
  startAdornment,
  endAdornment,
  textValuesNameTooltipText,
  isVerySmall,
  isTranslatePlaceholder = true,
  hideArrows,
  size,
  autoFocus,
  typographyWidth,
  onBlur,
  autoComplete
}) {
  const { t } = useTranslation();
  const { infoToast } = useToast();
  const defaultInputSize = useAppSelector((state) => state.customizer.inputSize);
  let cusInputSx = inputSx;

  const onCopyClick = () => {
    navigator.clipboard.writeText(value).then((e) => {
      infoToast('copied to your clipboard');
    });
  };

  if (isVerySmall) {
    if (!slotProps.htmlInput) slotProps.htmlInput = {};
    slotProps.htmlInput.sx = { paddingY: 0.5 };
  }

  if (readOnly) {
    if (!slotProps.htmlInput) slotProps.htmlInput = {};
    slotProps.htmlInput.readOnly = true;
  }

  if (hideArrows) {
    cusInputSx = {
      ...cusInputSx,
      '& input[type=number]': { MozAppearance: 'textfield' },
      '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
      '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
    };
  }

  const EndAdornmentJSX = () => (
    <InputAdornment position="end">
      <IconButton
        onMouseDown={(e) => e.preventDefault()}
        edge="end"
        size="small"
        disabled={!value}
        onClick={onCopyClick}>
        <IconCopy width={14} height={14} />
      </IconButton>
    </InputAdornment>
  );

  const fieldProps = {
    id: id,
    size: size || defaultInputSize,
    fullWidth: true,
    type: type,
    value: value === null ? '' : value,
    disabled: disabled,
    placeholder: isTranslatePlaceholder ? t(placeholder) : placeholder,
    ref: ref,
    sx: cusInputSx,
    name: name,
    slotProps,
    autoFocus: autoFocus,
    autoComplete,
    onChange: (e, ...dop) => {
      const value = e.target.value;
      if (value.length && maxLength) {
        if (value.length > maxLength) {
          dispatchCusEvent(ENUM_CUS_EVENT.translationToast, {
            message: t('Maximum field length', { length: maxLength }),
            type: ENUM_TOAST_TYPE.error
          });
          return;
        }
      }

      if (type == 'number' && value) {
        if (inputProps.max && inputProps.max < value) {
          dispatchCusEvent(ENUM_CUS_EVENT.translationToast, {
            message: t('Maximum value max', { value: inputProps.max }),
            type: ENUM_TOAST_TYPE.error
          });
          return;
        }
        if (inputProps.min && inputProps.min > value) {
          dispatchCusEvent(ENUM_CUS_EVENT.translationToast, {
            message: t('Minimum value min', { value: inputProps.min }),
            type: ENUM_TOAST_TYPE.error
          });
          return;
        }
      }
      onChange?.(e, ...dop);
    },
    onBlur: onBlur,
    rows: rows,
    multiline: !!rows,
    inputProps: { ...inputProps }
  };

  if (isRowReverse) {
    return (
      <Grid2 container alignItems={'center'} onClick={onClick}>
        <Grid2 size={1} minWidth={75}>
          <CustomTextField
            {...fieldProps}
            slotProps={{
              ...fieldProps.slotProps,
              input: {
                ...fieldProps.slotProps.input,
                startAdornment: startAdornment,
                endAdornment: endAdornment,
                ...(isCopyClick && { endAdornment: <EndAdornmentJSX /> })
              }
            }}
          />
        </Grid2>
        <Grid2 size={11} width={'auto'} pl={1}>
          <Stack
            display={'flex'}
            alignItems={'center'}
            flexDirection={'row'}
            gap={0.5}
            flexWrap={'wrap-reverse'}>
            <Typography
              fontSize={isVerySmall ? 12 : undefined}
              color={disabled ? 'textDisabled' : 'textPrimary'}>
              {typeof label == 'string' ? t(label) : label}
            </Typography>
            {tooltipText && <CusTooltip text={tooltipText} />}
          </Stack>
          <ShowContainer condition={subLabel}>
            <Typography color="info" variant="body2">
              {t(subLabel)}
            </Typography>
          </ShowContainer>
        </Grid2>
      </Grid2>
    );
  }

  if (isRow) {
    return (
      <Grid2 width={width} container alignItems={'center'} onClick={onClick} spacing={1}>
        <Grid2 size={'auto'}>
          <Stack
            display={'flex'}
            alignItems={'center'}
            flexDirection={'row'}
            gap={0.5}
            flexWrap={'wrap-reverse'}>
            <Typography
              color={disabled ? 'textDisabled' : 'textPrimary'}
              fontSize={isVerySmall ? 12 : undefined}
              width={typographyWidth}>
              {t(label)}
            </Typography>
            {tooltipText && <CusTooltip text={tooltipText} />}
          </Stack>
          <ShowContainer condition={subLabel}>
            <Typography color="info" variant="body2">
              {t(subLabel)}
            </Typography>
          </ShowContainer>
        </Grid2>

        <Grid2 size={'grow'}>
          <CustomTextField
            {...fieldProps}
            slotProps={{
              ...fieldProps.slotProps,
              input: {
                ...fieldProps.slotProps.input,
                startAdornment: startAdornment,
                endAdornment: endAdornment,
                ...(isCopyClick && { endAdornment: <EndAdornmentJSX /> })
              }
            }}
          />
        </Grid2>
      </Grid2>
    );
  }

  return (
    <Grid2 width={width} onClick={onClick}>
      <FormControl fullWidth sx={{ padding: '0' }}>
        <Stack display={'flex'} direction={'column'} gap={isVerySmall ? 0 : 0.5}>
          <ShowContainer condition={label}>
            <Stack
              display={'flex'}
              alignItems={'center'}
              flexDirection={'row'}
              gap={0.5}
              flexWrap={'wrap-reverse'}>
              <Typography
                color={disabled ? 'textDisabled' : 'textSecondary'}
                fontSize={isVerySmall ? 12 : undefined}>
                {typeof label == 'string' ? t(label) : label}
              </Typography>
              {tooltipText && <CusTooltip text={tooltipText} />}
            </Stack>
          </ShowContainer>
          <Stack direction={'row'} alignItems={'center'} gap={0.5}>
            <CustomTextField
              {...fieldProps}
              mt={0.5}
              slotProps={{
                ...fieldProps.slotProps,
                input: {
                  ...fieldProps.slotProps.input,
                  startAdornment: startAdornment,
                  endAdornment: endAdornment,
                  ...(isCopyClick && { endAdornment: <EndAdornmentJSX /> })
                }
              }}
            />
            {t(textValuesName)}
            <ShowContainer condition={textValuesNameTooltipText}>
              <CusTooltip text={textValuesNameTooltipText} />
            </ShowContainer>
          </Stack>
        </Stack>
      </FormControl>
    </Grid2>
  );
}
